import { useFormik } from "formik";
import React, { useMemo } from "react";
import { updateProfile, getCurrencies } from "../../utils/Api";
import * as Yup from "yup";
import clsx from "clsx";
import { Form, FormSelect } from "react-bootstrap";
import Autocomplete from "react-google-autocomplete";
import { getSelectedStreet, inputValidator } from "../../hooks/validator";
import limits from "../../hooks/limitations";
import {
  projectedAmount,
  projectedFrequency,
} from "../../utils/projectedValues";
import { toast } from "react-toastify";
import { useState, useEffect } from "react";

const AddressDetails = ({ accountType, forward, back, skiper, data }) => {
  const [countries, setCountries] = useState([]);
  const initialValues = useMemo(() => {
    return {
      payment_per_annum: data?.payment_per_annum || projectedFrequency[0],
      value_per_annum: data?.value_per_annum || projectedAmount[0],
      city: data.city || "",
      state: data.state || "",
      postcode: data.postcode || "",
      street: data.street || "",
      flat: data.flat || "",
      building: data.building || "",
      country_code: data.country_code || "AU",
      country: data.country || "Australia",
      address: data.address || "",
      location: data.location || "",
    };
  }, [data]);

  const validationSchema = Yup.object().shape({
    payment_per_annum: Yup.string(),
    value_per_annum: Yup.string(),
    country: Yup.string().max(limits.country).required().notOneOf(["none"]),
    state: Yup.string().max(limits.state).required().trim().notOneOf(["", " "]),
    city: Yup.string().max(limits.city).required().trim().notOneOf(["", " "]),
    // Fixed: Changed from .length() to .max() for more flexible validation
    postcode: Yup.string()
      .min(limits.user_zip?.min || 4, `Postcode must be at least ${limits.user_zip?.min || 4} characters`)
      .max(limits.user_zip?.max || 9, `Postcode must be at most ${limits.user_zip?.max || 9} characters`)
      .required("Postcode is required")
      .trim(),

    street: Yup.string()
      .max(limits.street)
      .required()
      .trim()
      .notOneOf(["", " "]),
    flat: Yup.string().max(limits.flat).notRequired(),
    building: Yup.string()
      .max(limits.building)
      .required()
      .trim()
      .notOneOf(["", " "]),
    address: Yup.string().max(limits.address),
  });

  useEffect(() => {
    const fetchCurrencies = async () => {
      try {
        const response = await getCurrencies();
        if (response?.code === "200" && response?.data?.payin_currencies) {
          setCountries(response.data.payin_currencies);
        }
      } catch (error) {
        console.error("Error fetching countries:", error);
        setCountries([]);
      }
    };

    fetchCurrencies();
  }, []);

  const {
    values,
    errors,
    touched,
    handleBlur,
    handleChange,
    handleSubmit,
    setValues,
    setFieldValue,
    getFieldProps,
  } = useFormik({
    initialValues,
    validationSchema,
    onSubmit: async (values) => {
      dataUpdateHandler(values);
    },
    validateOnBlur: true,
  });

  const OnCountryChange = (e) => {
    const selectedCountry = e.target.value;
    setValues((prev) => ({
      ...prev,
      country: selectedCountry,
      state: "",
      city: "",
      postcode: "",
      street: "",
      building: "",
      address: "",
    }));
  };

  const dataUpdateHandler = async (values) => {
    let payload = values;
    payload.location = values.country;
    if (/^\s*$/.test(payload.flat)) {
      delete payload["flat"];
    }
    if (
      payload.address === "" ||
      payload.address === undefined ||
      payload.address === " "
    ) {
      delete payload["address"];
    }
    const selectedCountryObj = countries.find(
      (c) => c.country === values.country
    );
    payload.country_code =
      selectedCountryObj?.country_code?.toUpperCase() || "AU"; // default fallback

    if (accountType === "business") {
      delete payload["value_per_annum"];
      delete payload["payment_per_annum"];
    }
    await Object.keys(payload).forEach((field) => {
      if (!payload[field]) {
        delete payload[field];
      }
    });

    forward(payload);
    await updateProfile(payload)
      .then((res) => {
        if (res.code === "200") {
          let user = JSON.parse(sessionStorage.getItem("remi-user-dt"));
          let local = {
            ...res?.data,
            is_digital_Id_verified: user?.is_digital_Id_verified,
          };
          sessionStorage.removeItem("remi-user-dt");
          sessionStorage.setItem("remi-user-dt", JSON.stringify(local));
        } else if (res.code === "400") {
          toast.error(res.message, {
            position: "bottom-right",
            hideProgressBar: true,
            autoClose: 2000,
          });
        }
      })
      .catch((err) => {
        toast.error(err.message, {
          position: "bottom-right",
          autoClose: 2000,
          hideProgressBar: true,
        });
      });
  };

  const skipHandler = () => {
    dataUpdateHandler(values);
    skiper();
  };

  const changeHandler = (e) => {
    inputValidator(e, handleChange);
  };

  return (
    <section className="kyc">
      <form onSubmit={handleSubmit} noValidate className="single-recipient">
        <div className="">
          <div className="">
            {accountType !== "business" && (
              <div className="row each-row">
                <p className="mb-3">
                  <span className="h5">Account Usage</span>
                  <span className="small">
                    &nbsp;(Utilization above tier 1 requires additional
                    verification documents.)
                  </span>
                </p>
                <div className="col-md-6">
                  <div className="input_field">
                    <p className="get-text">
                      Projected frequency of payments per annum
                      <span style={{ color: "red" }}>*</span>
                    </p>
                    <input
                      type="text"
                      name="payment_per_anmum"
                      value={values.payment_per_annum}
                      disabled
                      className={clsx(
                        "form-control bg-transparent",
                        {
                          "is-invalid":
                            touched.payment_per_annum &&
                            errors.payment_per_annum &&
                            values.payment_per_annum !== "" &&
                            null,
                        },
                        {
                          "is-valid":
                            touched.payment_per_annum &&
                            !errors.payment_per_annum &&
                            values.payment_per_annum !== "" &&
                            null,
                        }
                      )}
                    />
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="input_field">
                    <p className="get-text">
                      Projected value of payments per annum
                      <span style={{ color: "red" }}>*</span>
                    </p>
                    <input
                      type="text"
                      name="value_per_annum"
                      value={values.value_per_annum}
                      disabled
                      className={clsx(
                        "form-control bg-transparent",
                        {
                          "is-invalid":
                            touched.value_per_annum &&
                            errors.value_per_annum &&
                            values.value_per_annum !== "" &&
                            null,
                        },
                        {
                          "is-valid":
                            touched.value_per_annum &&
                            !errors.value_per_annum &&
                            values.value_per_annum !== "" &&
                            null,
                        }
                      )}
                    />
                  </div>
                </div>
              </div>
            )}

            <div className="row each-row">
              {accountType === "business" ? (
                <h5>Authorised Business Representative Address</h5>
              ) : (
                <h5>Your Address</h5>
              )}
              <div className="col-md-6 mb-3">
                <Form.Group className="form_label" controlId="country">
                  <p className="get-text">
                    Country<span style={{ color: "red" }}>*</span>
                  </p>
                  <FormSelect
                    value={values.country}
                    name="country"
                    id="country"
                    onChange={OnCountryChange}
                    className={clsx(
                      "bg-transparent",
                      { "is-invalid": touched.country && errors.country },
                      { "is-valid": touched.country && !errors.country }
                    )}
                  >
                    <option value="">Select Country</option>
                    {countries.map((item) => (
                      <option key={item.id} value={item.country}>
                        {item.country}
                      </option>
                    ))}
                  </FormSelect>
                </Form.Group>
              </div>
            </div>
            <div className="row each-row">
              <div className="col-md-12 mb-3">
                <Form.Group className="form_label" controlId="address">
                  <p className="get-text">Address</p>
                  <Autocomplete
                    apiKey={process.env.REACT_APP_GOOGLE_API_KEY}
                    onPlaceSelected={(value) => {
                      // Pass the currently selected country to preserve it
                      getSelectedStreet(value, setFieldValue, values.country);
                    }}
                    placeholder="Street Address, Company or P.O. box.. "
                    id="address"
                    name="address"
                    className="form-control"
                    options={{
                      types: [],
                      componentRestrictions: {
                        country:
                          countries
                            .find((c) => c.country === values.country)
                            ?.country_code?.toLowerCase() || "au",
                      },
                    }}
                    onChange={handleChange}
                    value={values.address}
                    key={values.country}
                  />
                </Form.Group>
              </div>
            </div>
            <div className="row each-row">
              <div className="col-md-4 mb-3">
                <Form.Group className="form_label" controlId="flat">
                  <p className="get-text">Unit/Apt No.</p>
                  <input
                    type="text"
                    name="flat"
                    value={values.flat}
                    maxLength={limits.flat}
                    onChange={changeHandler}
                    className={clsx(
                      "form-control bg-transparent",
                      {
                        "is-invalid":
                          touched.flat &&
                          errors.flat &&
                          values.flat !== "" &&
                          null,
                      },
                      {
                        "is-valid":
                          touched.flat &&
                          !errors.flat &&
                          values.flat !== "" &&
                          null,
                      }
                    )}
                    onBlur={handleBlur}
                  />
                </Form.Group>
              </div>
              <div className="col-md-4 mb-3">
                <Form.Group className="form_label" controlId="building">
                  <p className="get-text">
                    Building No.<span style={{ color: "red" }}>*</span>
                  </p>
                  <input
                    type="text"
                    name="building"
                    value={values.building}
                    maxLength={limits.building}
                    onChange={changeHandler}
                    className={clsx(
                      "form-control bg-transparent",
                      { "is-invalid": touched.building && errors.building },
                      {
                        "is-valid": touched.building && !errors.building,
                      }
                    )}
                    onBlur={handleBlur}
                  />
                </Form.Group>
              </div>
              <div className="col-md-4 mb-3">
                <Form.Group className="form_label" controlId="street_name">
                  <p className="get-text">
                    Street Name<span style={{ color: "red" }}>*</span>
                  </p>
                  <input
                    type="text"
                    name="street"
                    value={values.street}
                    maxLength={limits.street}
                    onChange={changeHandler}
                    className={clsx(
                      "form-control bg-transparent",
                      { "is-invalid": touched.street && errors.street },
                      {
                        "is-valid": touched.street && !errors.street,
                      }
                    )}
                    onBlur={handleBlur}
                  />
                </Form.Group>
              </div>
            </div>
            <div className="row each-row">
              <div className="col-md-4 mb-3">
                <Form.Group className="form_label" controlId="city">
                  <p className="get-text">
                    City/Suburb<span style={{ color: "red" }}>*</span>
                  </p>
                  <input
                    type="text"
                    name="city"
                    value={values.city}
                    maxLength={limits.city}
                    onChange={changeHandler}
                    className={clsx(
                      "form-control bg-transparent",
                      { "is-invalid": touched.city && errors.city },
                      {
                        "is-valid": touched.city && !errors.city,
                      }
                    )}
                    placeholder="city or suburb .."
                    onBlur={handleBlur}
                  />
                </Form.Group>
              </div>
              <div className="col-md-4 mb-3">
                <Form.Group className="form_label" controlId="postal">
                  <p className="get-text">
                    Zip/Postal Code<span style={{ color: "red" }}>*</span>
                  </p>
                  <input
                    type="text"
                    name="postcode"
                    value={values.postcode}
                    maxLength={limits.user_zip || 10}
                    onChange={changeHandler}
                    className={clsx(
                      "form-control bg-transparent",
                      { "is-invalid": touched.postcode && errors.postcode },
                      {
                        "is-valid": touched.postcode && !errors.postcode,
                      }
                    )}
                    onBlur={handleBlur}
                  />
                  {touched.postcode && errors.postcode && (
                    <div className="invalid-feedback">{errors.postcode}</div>
                  )}
                </Form.Group>
              </div>
              <div className="col-md-4 mb-3">
                <Form.Group className="form_label statess" controlId="state">
                  <p className="get-text">
                    State<span style={{ color: "red" }}>*</span>
                  </p>
                  <input
                    type="text"
                    name="state"
                    value={values.state}
                    maxLength={limits.state}
                    onChange={changeHandler}
                    className={clsx(
                      "form-control bg-transparent",
                      { "is-invalid": touched.state && errors.state },
                      {
                        "is-valid": touched.state && !errors.state,
                      }
                    )}
                    placeholder="state or province .."
                    onBlur={handleBlur}
                  />
                </Form.Group>
              </div>
            </div>
          </div>
        </div>
        <div className="next-step">
          <button
            type="button"
            className="SKip back-btn"
            onClick={() => back()}
          >
            Back
          </button>
          <button type="submit" className="login_button">
            Continue
            <img
              src="assets/img/home/Union.png"
              className="vission_image"
              alt="alt_image"
            />
          </button>
          <button type="button" className="SKip" onClick={() => skipHandler()}>
            Skip
          </button>
        </div>
      </form>
    </section>
  );
};

export default AddressDetails;
